import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { SMLRouterType } from './SMLRouterType';
import { extractLocationFromPath } from './extractLocationFromPath';
import { SMLRootParamList, SMLRouteNames, SMLRouteParts, SMLRoutes } from './SMLRoutes';
import { buildQuery } from '../utils/buildQuery';
import { smlRoutePath } from './smlRoutePath';

type SMLNavigateMethod = 'push' | 'replace';

export function useSMLRouter(): SMLRouterType {
  const navigate = useNavigate();
  const { pathname, key } = useLocation();
  const [searchParams] = useSearchParams();

  // https://github.com/remix-run/history/blob/main/docs/api-reference.md#locationkey
  const canGoBack = useMemo(() => key !== 'default', [key]);

  const popToRoot = useCallback(() => {
    // eslint-disable-next-line no-void
    void navigate(`${SMLRoutes.home}`);
  }, [navigate]);

  const _navigate = useCallback(
    (props: { routeParts: SMLRouteParts; params?: SMLRootParamList[SMLRouteNames]; method: SMLNavigateMethod }) => {
      const { routeParts, params, method } = props;
      const query = buildQuery(params);
      const path = `/${smlRoutePath(routeParts)}${query}`;
      // eslint-disable-next-line no-void
      void navigate(path, { replace: method === 'replace' });
    },
    [navigate]
  );

  const push = useCallback(
    (routeParts: SMLRouteParts, params?: SMLRootParamList[SMLRouteNames]) => {
      _navigate({ routeParts, params, method: 'push' });
    },
    [_navigate]
  );

  const replace = useCallback(
    (routeParts: SMLRouteParts, params?: SMLRootParamList[SMLRouteNames]) => {
      _navigate({ routeParts, params, method: 'replace' });
    },
    [_navigate]
  );

  const setParams = useCallback(
    (params: SMLRootParamList[SMLRouteNames]) => {
      if (!params) {
        return;
      }
      // Merge new params with existing search params
      const existingParams: Record<string, string> = {};
      searchParams.forEach((value, paramKey) => {
        existingParams[paramKey] = value;
      });
      const mergedParams = { ...existingParams, ...params };
      const query = buildQuery(mergedParams);
      const path = `${pathname}${query}`;
      // eslint-disable-next-line no-void
      void navigate(path, { replace: true });
    },
    [pathname, navigate, searchParams]
  );

  const goBack = useCallback(
    (expectedRoute: SMLRouteParts | undefined, params?: SMLRootParamList[SMLRouteNames]) => {
      /*
       * WEB: if you click in the email link someone sent you, which opens smart link edition, then after
       * saving the smart link you will land back in email instead of smart links details. To avoid that
       * we push the expected route instead
       */
      if (canGoBack) {
        // eslint-disable-next-line no-void
        void navigate(-1);
      } else {
        push(expectedRoute ?? SMLRoutes.home, params);
      }
    },
    [canGoBack, navigate, push]
  );

  return useMemo(
    () => ({
      push,
      replace,
      setParams,
      popToRoot,
      goBack,
      canGoBack,
    }),
    [push, replace, setParams, popToRoot, goBack, canGoBack]
  );
}

export function useSMLRoute(): SMLRouteParts | undefined {
  const { pathname } = useLocation();
  return useMemo(() => extractLocationFromPath(pathname), [pathname]);
}
